import createDependencyContext from 'adapter/in/common/dependenciesContext';
import ReadPresetField from 'use-case/presets/fields/read/readPresetField';
import ReadPresetForm from 'use-case/presets/form/readPresetFormClient';

import readWidgetDataUseCase, { ReadWidgetData } from 'use-case/widgets/data/read/readWidgetDataUseCase';
import readWidgetDataApiClient from 'adapter/out/client/widgets/data/readWidgetDataApiClient';

import readWidgetUseCase, { ReadWidget } from 'use-case/widget/read/readWidgetUseCase';
import readWidgetApiClient from 'adapter/out/client/widget/readWidgetApiClient';

import readPresetFormUseCase from 'use-case/presets/form/readPresetFormUseCase';
import readPresetFormApiClient from 'adapter/out/client/presets/form/readPresetFormApiClient';

import readPresetFieldUseCase from 'use-case/presets/fields/read/readPresetFieldUseCase';
import readPresetFieldApiClient from 'adapter/out/client/presets/fields/readPresetFieldApiClient';

import updateWidgetUseCase, { UpdateWidget } from 'use-case/widget/update/updateWidgetUseCase';
import updateWidgetApiClient from 'adapter/out/client/widget/updateWidgetApiClient';
import ReadWidgetDataCriteria, { ReadWidgetDataCriteriaBuilder } from 'use-case/widgets/data/read/readWidgetDataCriteria';
import { WidgetType } from 'domain/entity/widget';

export interface ICommonDependencies {
    readWidget: ReadWidget;
    readPresetForm: ReadPresetForm;
    readPresetField: ReadPresetField;
    updateWidget: UpdateWidget;
}

export interface IWidgetDataDependencies {
    readWidgetData: ReadWidgetData;
}

export interface IWidgetDataCriteriaDependencies {
    buildWidgetDataCriteria: (widgetId: string, widgetType: WidgetType, frameText?: string) => ReadWidgetDataCriteria;
    registerWidget?: () => () => void;
    triggerWidgetLoads?: boolean;
}

const CommonDependenciesContext = createDependencyContext<ICommonDependencies>();

const WidgetDataDependenciesContext = createDependencyContext<IWidgetDataDependencies>();

const WidgetDataCriteriaDependenciesContext = createDependencyContext<IWidgetDataCriteriaDependencies>();

export const getCommonDependencies = (
    fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
    baseUrl: string
): ICommonDependencies => ({
    readWidget: readWidgetUseCase(readWidgetApiClient(fetch, baseUrl)),
    readPresetForm: readPresetFormUseCase(readPresetFormApiClient(fetch, baseUrl)),
    readPresetField: readPresetFieldUseCase(readPresetFieldApiClient(fetch, baseUrl)),
    updateWidget: updateWidgetUseCase(updateWidgetApiClient(fetch, baseUrl)),
});

export const getWidgetDataDependencies = (
    fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
    baseUrl: string
): IWidgetDataDependencies => ({
    readWidgetData: readWidgetDataUseCase(readWidgetDataApiClient(fetch, baseUrl)),
});

export const getCriteriaDestination = (widgetType: WidgetType): string => {
    switch (widgetType) {
        case WidgetType.ANALYSIS:
            return 'analyses';
        case WidgetType.RICH_TEXT:
            return 'texts';
        default:
            return 'widgets';
    }
};

export const getWidgetDataCriteriaDependencies = (): IWidgetDataCriteriaDependencies => ({
    buildWidgetDataCriteria: (widgetId: string, widgetType: WidgetType) => {
        const destination = getCriteriaDestination(widgetType);
        return ReadWidgetDataCriteriaBuilder.create()
            .withWidgetId(widgetId)
            .withDestination(destination)
            .build();
    },
});

export const { Provider: CommonDependenciesContextProvider, useDependencies: useCommonDependencies } = CommonDependenciesContext;

export const {
    Provider: WidgetDataDependenciesContextProvider,
    useDependencies: useWidgetDataDependencies,
} = WidgetDataDependenciesContext;

export const {
    Provider: WidgetDataCriteriaDependenciesContextProvider,
    useDependencies: useWidgetDataCriteriaDependencies,
} = WidgetDataCriteriaDependenciesContext;
